import { useLocation } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { AsideActionPosition } from '~/types/global'
import { AsideActionContext } from '~/utils/contextes'

export function AsideActionProvider({ children }: { children: ReactNode }) {
  const [asideAction, setAsideAction] = useState<ReactNode | null>(null)
  const [returnAction, setReturnAction] = useState<ReactNode | null>(null)
  const [isWideAside, setIsWideAside] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [resetOnClose, setResetOnClose] = useState(true)
  const [position, setPosition] = useState<AsideActionPosition>('sidebar')
  const [availablePositions, setAvailablePositions] = useState<
    AsideActionPosition[]
  >(['sidebar'])
  const location = useLocation()
  const previousPathname = useRef<string | null>(null)

  const setAction = useCallback(
    ({
      action,
      isWide,
      position,
      availablePositions,
      returnTo
    }: {
      action: ReactNode
      isWide?: boolean
      position?: AsideActionPosition
      availablePositions?: AsideActionPosition[]
      returnTo?: ReactNode
    }) => {
      setAsideAction(action)
      setReturnAction(returnTo ?? null)
      setIsDirty(false)
      setIsWideAside(isWide ?? false)
      setPosition(position ?? 'sidebar')
      setAvailablePositions(availablePositions ?? ['sidebar'])
    },
    []
  )

  const resetAction = useCallback(() => {
    setAsideAction(returnAction)
    setReturnAction(null)
    setIsDirty(false)
    setResetOnClose(true)
  }, [returnAction])

  // Navigation closes everything, including the aside a quick action would otherwise restore
  const clearAction = useCallback(() => {
    setAsideAction(null)
    setReturnAction(null)
    setIsDirty(false)
    setResetOnClose(true)
  }, [])

  useEffect(() => {
    // Sometimes asideAction component set isDirty to true while being unmounted via resetAction()
    // This is a workaround to prevent this behavior
    if (asideAction === null && isDirty) {
      setIsDirty(false)
    }
  }, [asideAction, isDirty])

  // Reset aside action when navigating away from the page
  useEffect(() => {
    const currentPathname = location.pathname
    if (
      previousPathname.current !== null &&
      previousPathname.current !== currentPathname &&
      asideAction !== null
    ) {
      clearAction()
    }
    previousPathname.current = currentPathname
  }, [location.pathname, asideAction, clearAction])

  return (
    <AsideActionContext.Provider
      value={{
        asideAction,
        isDirty,
        resetOnClose,
        isWide: isWideAside,
        setAsideAction: setAction,
        setPosition,
        setIsDirty,
        setResetOnClose,
        position,
        availablePositions,
        resetAction,
        clearAction
      }}
    >
      {children}
    </AsideActionContext.Provider>
  )
}
